import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { membershipOptionsApi, exemptionCodesApi } from '../lib/api';
import PayPalPayment from '../components/PayPalPayment';

export default function SignUpPage() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    email: "",
    password: "",
    golf_handicap: "",
    ghin_number: "",
    membership: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [membershipError, setMembershipError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [exemptionCode, setExemptionCode] = useState('');
  const [exemptionValid, setExemptionValid] = useState(false);
  const [exemptionChecking, setExemptionChecking] = useState(false);
  const [exemptionError, setExemptionError] = useState('');
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [membershipOptions, setMembershipOptions] = useState([]);

  useEffect(() => {
    fetchMembershipOptions();
    }, []);
    const fetchMembershipOptions = async () => {
    try {
    const options = await membershipOptionsApi.getAll();
    setMembershipOptions(options);
    } catch (err) {
    console.error('Failed to load membership options:', err);
    }
    };

  // Get the selected membership price
  const selectedOption = membershipOptions.find(o => o.name === form.membership);
  const membershipPrice = selectedOption ? (parseFloat(selectedOption.price) || 0) : 0;

  // Check if all required form fields are filled (excluding payment)
  const isFormValid = () => {
    return (
      form.first_name.trim() &&
      form.last_name.trim() &&
      form.phone_number.trim() &&
      form.email.trim() &&
      form.password.trim() &&
      form.membership
    );
  };

  // Handle North token — called with token string after card is tokenized
  const handlePayPalApprove = useCallback(async ({ orderID }) => {
    setError("");
    setPaymentError("");
    setLoading(true);

    try {
      const userData = {
        ...form,
        handicap: form.golf_handicap ? String(form.golf_handicap).replace(/\.$/, '') : null,
        ghin_number: form.ghin_number.trim() || null,
        paypal_order_id: orderID,
        exemption_code: exemptionValid ? exemptionCode.trim() : null,
      };

      await signup(userData);
      alert("Account created successfully! Please log in.");
      navigate("/login");
    } catch (err) {
      let message = "Signup failed. Please try again.";

      if (typeof err === 'string') {
        message = err;
      } else if (err?.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err?.detail) {
        message = err.detail;
      } else if (err?.message) {
        message = err.message;
      }

      const lowerMessage = message.toLowerCase();

      if (lowerMessage.includes("email already") ||
          lowerMessage.includes("already registered") ||
          lowerMessage.includes("email exists")) {
        message = "This email is already registered. Please use a different email or try logging in.";
      } else if (lowerMessage.includes("invalid email")) {
        message = "Please enter a valid email address.";
      } else if (lowerMessage.includes("password")) {
        message = "Password must be at least 6 characters long.";
      } else if (lowerMessage.includes("declined")) {
        setPaymentError(message);
        setLoading(false);
        return;
      }

      setError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  }, [form, signup, navigate, exemptionCode, exemptionValid]);

  // Handle free membership signup (no payment)
  const handleFreeSignup = useCallback(async () => {
    setError("");
    setLoading(true);

    try {
      const userData = {
        ...form,
        handicap: form.golf_handicap ? String(form.golf_handicap).replace(/\.$/, '') : null,
        ghin_number: form.ghin_number.trim() || null,
        exemption_code: exemptionValid ? exemptionCode.trim() : null,
      };

      await signup(userData);
      alert("Account created successfully! Please log in.");
      navigate("/login");
    } catch (err) {
      let message = "Signup failed. Please try again.";
      if (typeof err === 'string') message = err;
      else if (err?.response?.data?.detail) message = err.response.data.detail;
      else if (err?.detail) message = err.detail;
      else if (err?.message) message = err.message;

      const lowerMessage = message.toLowerCase();
      if (lowerMessage.includes("email already") || lowerMessage.includes("already registered") || lowerMessage.includes("email exists")) {
        message = "This email is already registered. Please use a different email or try logging in.";
      }

      setError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  }, [form, signup, navigate, exemptionCode, exemptionValid]);

  const handlePaymentError = useCallback((errorMsg) => {
    setPaymentError(errorMsg);
  }, []);

  const handleValidateCode = async () => {
    const code = exemptionCode.trim();
    if (!code) return;
    setExemptionChecking(true);
    setExemptionError('');
    try {
      await exemptionCodesApi.validate(code);
      setExemptionValid(true);
    } catch (err) {
      setExemptionValid(false);
      setExemptionError(err.message || 'Invalid code');
    } finally {
      setExemptionChecking(false);
    }
  };

  const handleClearCode = () => {
    setExemptionCode('');
    setExemptionValid(false);
    setExemptionError('');
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (555) 555-5555
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({ ...form, phone_number: formatted });
  };

  const handleHandicapChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setForm(prev => ({ ...prev, golf_handicap: value }));
      return;
    }
    
    const regex = /^-?\d*\.?\d{0,1}$/;
    if (regex.test(value)) {
      const numValue = parseFloat(value);
      if (value === '-' || value === '.' || value.endsWith('.') || 
          (!isNaN(numValue) && numValue >= -10 && numValue <= 30)) {
            setForm(prev => ({ ...prev, golf_handicap: value }));
      }
    }
  };

  const handleHandicapBlur = (e) => {
    const value = e.target.value;
    if (value.endsWith('.')) {
      setForm(prev => ({ ...prev, golf_handicap: value.slice(0, -1) }));
    }
  };

  const fields = [
    { key: "first_name", label: "First Name *", type: "text", placeholder: "John", required: true },
    { key: "last_name", label: "Last Name *", type: "text", placeholder: "Doe", required: true },
    { key: "phone_number", label: "Phone Number *", type: "tel", placeholder: "(555) 555-5555", required: true },
    { key: "email", label: "Email Address *", type: "email", placeholder: "john@example.com", required: true },
    { key: "golf_handicap", label: "Golf Handicap", type: "text", placeholder: "e.g., 12", required: false },
    { key: "ghin_number", label: "GHIN Number", type: "text", placeholder: "e.g., 1234567", required: false }
  ];

  return (
    <div className="login-page">
      <h1>Membership Sign Up</h1>
    
      {error && (
        <div className="error-banner">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="20" height="20">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <div className="login-form">
        {fields.map((field) => (
          <div className="form-group" key={field.key}>
            <label htmlFor={field.key}>{field.label}</label>
            <input
              id={field.key}
              type={field.type}
              placeholder={field.placeholder}
              value={form[field.key]}
              onChange={
                field.key === "phone_number" ? handlePhoneChange
                : field.key === "golf_handicap" ? handleHandicapChange
                : field.key === "ghin_number" ? (e) => setForm({ ...form, ghin_number: e.target.value.replace(/\D/g, '').slice(0, 10) })
                : (e) => setForm({ ...form, [field.key]: e.target.value })
              }
              onBlur={
                field.key === "golf_handicap" ? handleHandicapBlur
                : (e) => setForm({ ...form, [field.key]: e.target.value })
              }
              required={field.required}
              disabled={loading}
            />
          </div>
        ))}

        <div className="form-group">
          <label htmlFor="password">Password *</label>
          <div className="password-input-wrapper">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* ✅ Membership Options - Original Styling */}
        <div className={`form-group membership-section ${membershipError ? 'has-error' : ''}`}>
          <label className="section-label">
            Select Membership Type *
            {membershipError && <span className="error-indicator"> - Required</span>}
          </label>
          <div className="membership-options">
            {membershipOptions.map(option => (
              <div
                key={option.id}
                className={`membership-option ${form.membership === option.name ? 'selected' : ''}`}
                onClick={() => {
                  setForm({
                    ...form,
                    membership: option.name,
                    membershipPrice: option.price
                  });
                  setMembershipError(false);
                }}
              >
                <input
                  type="radio"
                  name="membership"
                  value={option.name}
                  checked={form.membership === option.name}
                  onChange={(e) => {
                    setForm({
                      ...form,
                      membership: e.target.value,
                      membershipPrice: option.price
                    });
                    setMembershipError(false);
                  }}
                  required
                />
                <div className="option-content">
                  <div className="option-text">
                    <span className="option-label">{option.name}</span>
                    {option.description && <span className="option-subtitle">{option.description}</span>}
                  </div>
                  <span className="option-price">${Number(option.price).toFixed(2)}</span>
                  <div className="radio-indicator"></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Exemption Code Section */}
        {form.membership && membershipPrice > 0 && (
          <div className="exemption-code-section">
            <label className="exemption-label">Have a promo code?</label>
            <div className="exemption-input-row">
              <input
                type="text"
                placeholder="Enter code"
                value={exemptionCode}
                onChange={(e) => {
                  setExemptionCode(e.target.value.toUpperCase());
                  if (exemptionValid) {
                    setExemptionValid(false);
                    setExemptionError('');
                  }
                }}
                className={`exemption-input ${exemptionValid ? 'valid' : ''} ${exemptionError ? 'invalid' : ''}`}
                disabled={exemptionValid}
              />
              {!exemptionValid ? (
                <button
                  type="button"
                  onClick={handleValidateCode}
                  disabled={!exemptionCode.trim() || exemptionChecking}
                  className="exemption-btn"
                >
                  {exemptionChecking ? 'Checking...' : 'Apply'}
                </button>
              ) : (
                <button type="button" onClick={handleClearCode} className="exemption-btn clear">
                  Clear
                </button>
              )}
            </div>
            {exemptionError && <p className="exemption-error">{exemptionError}</p>}
            {exemptionValid && <p className="exemption-success">Code applied — membership fee waived!</p>}
          </div>
        )}

        {/* Payment section — appears after membership is selected and form is valid (skip if exempt) */}
        {membershipPrice > 0 && !exemptionValid && isFormValid() && (
          <PayPalPayment
            amount={membershipPrice}
            description="SAGA Golf Membership"
            onApprove={handlePayPalApprove}
            onError={handlePaymentError}
            loading={loading}
            submitLabel={`Sign Up & Pay — $${membershipPrice.toFixed(2)}`}
            error={paymentError}
          />
        )}

        {/* Prompt to complete form */}
        {membershipPrice > 0 && !exemptionValid && !isFormValid() && form.membership && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.5rem' }}>
            Fill in all required fields above to continue to payment.
          </p>
        )}

        {/* Prompt to complete form (exempt case) */}
        {membershipPrice > 0 && exemptionValid && !isFormValid() && form.membership && (
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.5rem' }}>
            Fill in all required fields above to continue.
          </p>
        )}

        {/* Exempt signup button (code applied, paid membership) */}
        {form.membership && membershipPrice > 0 && exemptionValid && (
          <button
            type="button"
            onClick={handleFreeSignup}
            disabled={loading || !isFormValid()}
            style={{
              width: '100%',
              marginTop: '1rem',
              padding: '0.875rem 1.5rem',
              background: isFormValid() ? 'var(--primary, #1a472a)' : '#9ca3af',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: isFormValid() ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            {loading ? 'Creating Account...' : 'Sign Up'}
          </button>
        )}

        {/* Free membership sign up button (e.g. Student Golfer) */}
        {form.membership && membershipPrice === 0 && (
          <button
            type="button"
            onClick={handleFreeSignup}
            disabled={loading || !isFormValid()}
            style={{
              width: '100%',
              marginTop: '1rem',
              padding: '0.875rem 1.5rem',
              background: isFormValid() ? 'var(--primary, #1a472a)' : '#9ca3af',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: isFormValid() ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            {loading ? 'Creating Account...' : 'Sign Up — Free'}
          </button>
        )}

        <p>
          Already have an account?{" "}
          <span
            style={{ color: "var(--primary)", cursor: "pointer", fontWeight: 500 }}
            onClick={() => navigate("/login")}
          >
            Sign In
          </span>
        </p>
      </div>

      <style jsx>{`
        /* Error Banner */
        .error-banner {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem 1.25rem;
          background: #fef2f2;
          border: 2px solid #ef4444;
          border-radius: var(--radius-lg);
          color: #991b1b;
          margin-bottom: 1.5rem;
          animation: slideDown 0.3s ease-out;
        }

        .error-banner svg {
          flex-shrink: 0;
          color: #ef4444;
        }

        .error-banner span {
          font-weight: 500;
          line-height: 1.5;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Membership Section Error State */
        .membership-section.has-error {
          border-color: #ef4444;
          background: #fef2f2;
          animation: shake 0.4s ease-in-out;
        }

        .error-indicator {
          color: #ef4444;
          font-weight: 700;
          font-size: 0.875rem;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        /* Password Input Wrapper */
        .password-input-wrapper {
          position: relative;
          width: 100%;
        }

        .password-input-wrapper input {
          padding-right: 3rem;
        }

        .password-toggle {
          position: absolute;
          right: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 0.5rem;
          cursor: pointer;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s ease;
        }

        .password-toggle:hover {
          color: var(--primary);
        }

        /* Membership Section - Original Styling */
        .membership-section {
          margin: 1.5rem 0;
          padding: 1.5rem;
          background: #f9fafb;
          border-radius: var(--radius-lg);
          border: 1px solid var(--border);
        }

        .section-label {
          display: block;
          font-weight: 600;
          font-size: 1rem;
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .membership-options {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .membership-option {
          position: relative;
          display: flex;
          align-items: center;
          padding: 1.25rem;
          background: white;
          border: 2px solid var(--border);
          border-radius: var(--radius-lg);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .membership-option:hover {
          border-color: var(--primary);
          box-shadow: 0 4px 12px rgba(13, 148, 136, 0.1);
        }

        .membership-option.selected {
          border-color: var(--primary);
          background: rgba(13, 148, 136, 0.05);
          box-shadow: 0 4px 12px rgba(13, 148, 136, 0.15);
        }

        .membership-option input[type="radio"] {
          position: absolute;
          opacity: 0;
          width: 0;
          height: 0;
        }

        .option-content {
          flex: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1rem;
        }

        .option-text {
	  flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
	  min-width:0;
        }

        .option-label {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 1rem;
        }

        .option-subtitle {
          font-weight: 400;
          color: var(--text-secondary);
          font-size: 0.771rem;
        }

        .option-price {
          font-weight: 700;
          color: var(--primary);
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .radio-indicator {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          border: 2px solid var(--border);
          position: relative;
          transition: all 0.2s ease;
          flex-shrink: 0;
        }

        .membership-option:hover .radio-indicator {
          border-color: var(--primary);
        }

        .membership-option.selected .radio-indicator {
          border-color: var(--primary);
          background: var(--primary);
        }

        .membership-option.selected .radio-indicator::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: white;
        }

        /* Exemption Code */
        .exemption-code-section {
          margin-top: 1rem;
          padding: 1rem 1.25rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: var(--radius-lg, 12px);
        }
        .exemption-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }
        .exemption-input-row {
          display: flex;
          gap: 0.5rem;
        }
        .exemption-input {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          font-family: monospace;
          letter-spacing: 0.05em;
          background: white;
        }
        .exemption-input:focus {
          outline: none;
          border-color: var(--primary, #0d9488);
          box-shadow: 0 0 0 2px rgba(13, 148, 136, 0.1);
        }
        .exemption-input.valid {
          border-color: #22c55e;
          background: #f0fdf4;
        }
        .exemption-input.invalid {
          border-color: #ef4444;
        }
        .exemption-btn {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 8px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          background: var(--primary, #0d9488);
          color: white;
          white-space: nowrap;
        }
        .exemption-btn:hover:not(:disabled) { opacity: 0.9; }
        .exemption-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .exemption-btn.clear {
          background: #e5e7eb;
          color: #374151;
        }
        .exemption-btn.clear:hover { background: #d1d5db; }
        .exemption-error {
          margin: 0.375rem 0 0;
          font-size: 0.8rem;
          color: #dc2626;
        }
        .exemption-success {
          margin: 0.375rem 0 0;
          font-size: 0.8rem;
          color: #16a34a;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .membership-section {
            padding: 1rem;
          }

          .membership-option {
            padding: 1rem;
          }

          .option-content {
            align-items: flex-start;
          }

          .option-price {
            font-size: 1rem;
          }
        }
      `}</style>
    </div>
  );
}
